import {Badge, message} from 'antd';
import {History, Location} from 'history';
import _ from 'lodash';
import React, {useState} from "react";
import {useHistory, useLocation} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../../hooks';
import {buildStudyCohortRoute, buildStudyPeriodRoute, Routes} from '../../../pages/routes';
import {isSharedProject, Project} from '../../../types/Project';
import {setCurrentReportAndRefresh} from '../../HealthcareUsageReport/actions';
import {emptyCohortReport} from '../../HealthcareUsageReport/reducers';
import {setCurrentPathwayAndRefresh} from '../../PathwayViewer/actions';
import {deleteStudy, selectColleagues} from '../cohortBuilderSlice';
import styles from './ProjectItem.module.scss';
import {CalendarDate} from "../icons/CalendarDate";
import {Delete} from "../../../components/common/actions/Delete";
import colors from '../../../style/theme.module.scss'
import {ShareAltOutlined} from '@ant-design/icons';
import {ShareProject} from "./ShareProject";
import {UserDetails} from "../../../actions/api";
import {displayShareDate} from "./shareDate";
import classNames from 'classnames';
import {apiMarkSharedProjectRead} from "../cohortBuilderApi";
import {findDataset} from "../DatasetPicker/DatasetPickerUtil";

interface ProjectProps {
    study: Project;
}

const colleagueName = (username: string, colleagues: UserDetails[] | undefined) => {
    const colleague = (colleagues ?? []).find(colleague => colleague.username === username)
    return colleague === undefined ? username : `${colleague.firstName} ${colleague.lastName}`
}

const ShareProjectButton = ({project, setOpenModal}: {project: Project, setOpenModal: (isOpen: boolean) => void}) => {
    if (isSharedProject(project)) {
        return <span>{displayShareDate(new Date(), new Date(project.sharedItem.createdOn))}</span>
    }
    return <span
        onClick={() => {return setOpenModal(true);}}
        className={styles['share-wrapper']}>
        <ShareAltOutlined className={styles['share-icon']}/>
        <span>Share this project</span>
    </span>
}

export const DatasetLabels = ({study}: ProjectProps) => {
    const datasets = Array.from(new Set(study.queries.map(i => i.dataset)))
        .map(i => {
            const j = findDataset(i)!
            return <span key={j.key}>{j.icon} {j.label}</span>
        })
    if (datasets.length > 1) {
        datasets.splice(1, 0, <span key={'&'}> & </span>)
    }
    return <div className={styles.datasets}>
        {datasets}
    </div>
}

const ProjectItem = ({ study }: ProjectProps) => {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const history = useHistory()
    const editStudy = !study.example
    const unreadCount = study.queries.filter(query => query.unread).length
    const [openModal, setOpenModal] = useState(false);
    const colleagues = useAppSelector(selectColleagues)

    const selectStudy = (location: Location, project: Project, history: History) => {
        const trackableQueries = _.filter(project.queries, { trackable: true })
        switch (location.pathname) {
            case Routes.Projects:
                if (isSharedProject(project) && project.sharedItem.unread) {
                    apiMarkSharedProjectRead(project.id)
                }
                if (project.queries.length === 0) {
                    history.push(buildStudyPeriodRoute(project.id))
                } else {
                    const studyId = project.queries[0].id
                    history.push(buildStudyCohortRoute(project.id, studyId))
                }
                break
            case Routes.MyStudiesHRU:
                if (trackableQueries.length > 0) {
                    dispatch(setCurrentReportAndRefresh(emptyCohortReport(trackableQueries[0].id)))
                    history.push(Routes.MyHRU)
                } else {
                    message.warning('This Project does not have any trackable cohorts and therefore it is not possible to create a HRU report.');
                }
                break
            case Routes.MyStudiesPWV:
                if (trackableQueries.length > 0) {
                    dispatch(setCurrentPathwayAndRefresh())
                    history.push(Routes.MyPWV)
                } else {
                    message.warning('This Study does not have any trackable cohorts and therefore it is not possible to create a pathway to explore.');
                }
                break
            default:
                console.log('unknown page...', location.pathname)
                break
        }
    }

    return <>
        <div className={styles['project-background']}>
        <div className={classNames({[styles['project-unread']]: isSharedProject(study) && study.sharedItem.unread})}/>
        <div
            className={classNames(styles['project'])}
            onClick={() => selectStudy(location, study, history)}
            data-testid="project"
        >
        <div className={styles.date}>
            <CalendarDate/>
            {new Date(study.createdOn!).toLocaleDateString('en-GB')}
        </div>
        <div className={styles.content}>
            <div className={styles.title}>{study.title}</div>
            <div className={styles.description}>{study.description}</div>
            { isSharedProject(study) ?
                <div className={styles['shared-by-wrapper']}>
                    <span className={styles['shared-by-label']}>Shared by </span>
                    <span className={styles['shared-by-content']}>{colleagueName(study.sharedItem.ownerId, colleagues)}</span>
                </div> :
                null
            }
        </div>
            <DatasetLabels study={study}/>
            { editStudy ?
                <div className={styles.icons} onClick={e => e.stopPropagation()}>
                    <Delete
                        tooltip="delete project"
                        onDelete={() => { dispatch(deleteStudy(study.id, isSharedProject(study)))}}
                        icon="m"
                        text="Delete"
                    />
                { unreadCount > 0 ?
                    <span className={styles['new-results-wrapper']}>
                        <span>New Results</span>
                        <Badge
                            className={styles['new-results']}
                            color={colors.vantageBlue}
                            count={study.queries.filter(query => query.unread).length}
                        />
                    </span> : null
                }
                <ShareProjectButton project={study} setOpenModal={setOpenModal}/>
            </div> : null
        }
    </div>
    </div>
        { !isSharedProject(study) && editStudy
            ? <ShareProject project={study} open={openModal} setOpen={setOpenModal}/>
            : null
        }
    </>
}

export default ProjectItem